import { CheckIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Button, Flex, Menu, MenuButton, MenuItem, MenuList, Portal, Text, useToast } from "@chakra-ui/react";
import { t } from "i18next";
import { useState } from "react";

import { BseAnnouncementDto, BseQuestionDto, BseQuestionStatus } from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { displayDateWithTime } from "../../../../../common/formatting/displayDateWithTime";
import { useAppDispatch, useAppSelector } from "../../../../../common/redux/hooks";
import { setSupplierQuestionStatus } from "../../../../../common/redux/reducers/basicSourcingEventReducer";
import { Explanation } from "../../../../../common/support/Explanation/Explanation";

interface Props {
  canChangeQuestionStatus: boolean;
  question: BseQuestionDto;
  announcements: BseAnnouncementDto[];
  changeStatus: (status: BseQuestionStatus) => Promise<boolean>;
}

export const SupplierQuestionMeta = ({ canChangeQuestionStatus, question, changeStatus }: Props) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const sourcingEvent = useAppSelector((state) => state.basicSourcingEvent.state);
  const authState = useLoggedInWithOrgContextState();
  const askedByIsMe =
    question.askedByPerson.id === authState.me.id && question.askedByOrgName === authState.selectedOrg.name;
  const askedByLabel = askedByIsMe ? t("you") : `${question.askedByPerson.firstName}`;

  const [isLoading, setIsLoading] = useState(false);

  const handleStatusChange = async (nextStatus: BseQuestionStatus): Promise<void> => {
    if (isLoading) return;
    if (nextStatus === question.status) return;
    setIsLoading(true);
    const statusUpdateSucceeded = await changeStatus(nextStatus);
    setIsLoading(false);
    if (statusUpdateSucceeded && sourcingEvent) {
      toast({ title: t("Status changed!"), status: "success" });
      dispatch(setSupplierQuestionStatus({ question, status: nextStatus }));
    } else {
      toast({ title: t("Could not change status!"), status: "error" });
    }
  };

  return (
    <>
      <Flex borderRadius={"10px"} w="100%">
        <Flex w="100%" alignItems={"center"}>
          <Text pr="2" fontSize={"sm"}>
            {t("Asked by")} <b>{askedByLabel}</b> {t("in")} <b>{question.askedByOrgName}</b> {t("to")}{" "}
            <b>{question.askedToOrganization}</b>, {displayDateWithTime(question.askedAt)}
          </Text>
          <Explanation
            enabled={!canChangeQuestionStatus}
            text={t("Only admins and creators can change the status for questions")}
          >
            <Menu isLazy>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                isDisabled={!canChangeQuestionStatus}
                isLoading={isLoading}
                rightIcon={<ChevronDownIcon />}
                bg="smBackground"
                border="1px solid"
                borderColor="smBorder"
                color={question.status === BseQuestionStatus.Resolved ? "smPrimary" : "smDanger"}
                rounded="md"
                px="2"
                mr="2"
              >
                {question.status === BseQuestionStatus.Resolved ? t("Resolved") : t("Unresolved")}
              </MenuButton>
              <Portal>
                <MenuList zIndex={2000}>
                  <MenuItem fontSize="sm" py={1} onClick={() => handleStatusChange(BseQuestionStatus.Unresolved)}>
                    <Flex align="center" justify="space-between" width="full">
                      <Text>{t("Unresolved")}</Text>
                      {question.status === BseQuestionStatus.Unresolved ? <CheckIcon color="smPrimary" /> : null}
                    </Flex>
                  </MenuItem>
                  <MenuItem fontSize="sm" py={1} onClick={() => handleStatusChange(BseQuestionStatus.Resolved)}>
                    <Flex align="center" justify="space-between" width="full">
                      <Text>{t("Resolved")}</Text>
                      {question.status === BseQuestionStatus.Resolved ? <CheckIcon color="smPrimary" /> : null}
                    </Flex>
                  </MenuItem>
                </MenuList>
              </Portal>
            </Menu>
          </Explanation>
        </Flex>
      </Flex>
    </>
  );
};
